import { assign, createMachine, sendParent } from 'xstate';
import { EcommMachineEvents } from './types/ecommMachine.types';
import { NavigationSections } from '../constants';
import {
  EntryPointEvents,
  EntryPointMachineContext,
  EntryPointMachineEvents,
  EntryPointMachineStates,
  EntryPointStates,
} from './types/entrypointMachine.types';
import authorizeCard from '../networkRequests/debitCard/authorizeCard';
import getApplicationId from '../utils/commonState/getApplicationId';
import { PropagateNetworkErrors } from './actions/genericActions';
import { identify, page, track } from './actions/segment';
import { MakeStateCallingQueryOrderWithCartValidation } from './refresh-order';
import { ChannelTypes, FormTypes } from '../gql/graphql';
import { PostDecisionStates } from './types/postDecisionMachine.types';

export default createMachine<EntryPointMachineContext, EntryPointMachineEvents, EntryPointMachineStates>(
  {
    id: 'EntryPoint',
    initial: EntryPointStates.Initial,
    context: {} as EntryPointMachineContext,
    on: {
      [EntryPointEvents.TRACK]: {
        actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
      },
      [EntryPointEvents.PAGE]: {
        actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
      },
      [EntryPointEvents.IDENTIFY]: {
        actions: (context, event: any) => identify(event?.data?.customerSorId, event?.data?.payload),
      },
    },
    states: {
      [EntryPointStates.Initial]: {
        tags: 'Loading',
        entry: ['log'],
        always: [
          { target: EntryPointStates.OTPRequest, cond: 'OTPRequest' },
          { target: EntryPointStates.PreApproval, cond: 'PreApproval' },
          { target: EntryPointStates.BankAccountError, cond: 'BankAccountError' },
          { target: EntryPointStates.DebitCardError, cond: 'DebitCardError' },
          { target: EntryPointStates.LeaseDeclined, cond: 'LeaseDeclined' },
          { target: EntryPointStates.LoanDeclined, cond: 'LoanDeclined' },
        ],
      },
      [EntryPointStates.OTPRequest]: {
        id: 'OTPRequest',
        initial: 'waiting',
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
        states: {
          waiting: {
            on: {
              [EntryPointEvents.SEND_TOKEN]: {
                type: 'final',
                actions: [
                  sendParent((_context, event) => ({
                    type: EcommMachineEvents.PHONE_SUBMITTED,
                    data: { cellPhone: event.data.cellPhone },
                  })),
                ],
              },
              [EntryPointEvents.SKIP_OTP]: {
                type: 'final',
                actions: [
                  assign({ cellPhone: (_, event) => event.data.cellPhone, didSkipOTP: true }),
                  sendParent((context) => ({
                    type: EcommMachineEvents.SKIP_OTP_EVENT,
                    data: { ...context },
                  })),
                ],
              },
            },
          },
        },
      },
      [EntryPointStates.PreApproval]: {
        initial: 'callingQueryOrder',
        states: {
          callingQueryOrder: MakeStateCallingQueryOrderWithCartValidation('waiting'),
          waiting: { on: { [EcommMachineEvents.NEXT]: { target: 'callingAuthorizeCard', actions: assign({ authorizeCardErrors: [] }) } } },
          callingAuthorizeCard: {
            tags: 'callingAuthorizeCard',
            invoke: {
              src: (context: EntryPointMachineContext, event: EntryPointMachineEvents) => authorizeCard(getApplicationId(context.application)),
              onDone: [
                {
                  cond: (_context, event) => !!event.data.hardDeclineError,
                  target: '#EntryPoint.Done',
                  actions: [
                    assign({ authorizeCardErrors: (context, event) => event.data.authorizeCardErrors }),
                    sendParent((context) => ({
                      type: EcommMachineEvents.LONG_FORM,
                      data: {
                        ...context,
                        source: 'Declined',
                      },
                    })),
                    track('Debit Card Error', { errType: 'HardDecline' }),
                  ],
                },
                {
                  cond: (_context, event) => !!event.data.softDeclineError,
                  target: '#EntryPoint.Done',
                  actions: [
                    assign({
                      application: (_, event) => event?.data?.application,
                      authorizeCardErrors: (context, event) => event.data.authorizeCardErrors,
                    }),
                    sendParent((context) => ({
                      type: EcommMachineEvents.LONG_FORM,
                      data: { ...context, source: 'Error' },
                    })),
                    (context, event) => {
                      track('Debit Card Error', { errType: 'SoftDecline', decisionCode: event.data.softDeclineError?.extensions?.decisionCode })(
                        context,
                        event,
                      );
                    },
                  ],
                },
                {
                  cond: (_context, event) => !!event.data.authorizeCardErrors?.length,
                  target: 'waiting', // let the page handle it
                  actions: [assign({ authorizeCardErrors: (_context, event) => event.data.authorizeCardErrors })],
                },
                // OR-2997: initial payment authorized, the app is approved and the rest belongs to
                // post-decision-machine. The approved application comes off the authorizeCard result
                // so post-decision-machine does not start on the preApproved copy in this context.
                // ApprovalConfirmation has no requery to correct it, and setAppStatusParams reads it
                // on first render. The page is named here rather than derived because long form
                // ecomm skips the Approval landing page an approved status derives to.
                {
                  type: 'final',
                  cond: 'isEcommLongForm',
                  actions: sendParent((context, event) => ({
                    type: EcommMachineEvents.POST_DECISION,
                    data: {
                      ...context,
                      application: event?.data?.application ?? context.application,
                      postDecisionStartingPage: PostDecisionStates.ContractSummaryEcomm,
                    },
                  })),
                },
                {
                  target: '#EntryPoint.Done',
                  actions: sendParent((context, event) => ({
                    type: EcommMachineEvents.POST_DECISION,
                    data: {
                      ...context,
                      application: event?.data?.application ?? context.application,
                      postDecisionStartingPage: PostDecisionStates.ApprovalConfirmation,
                    },
                  })),
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
        },
        on: {
          [EntryPointEvents.APPROVED]: [
            {
              type: 'final',
              cond: 'isEcommLongForm',
              actions: sendParent((context) => ({
                type: EcommMachineEvents.POST_DECISION,
                data: { ...context, postDecisionStartingPage: PostDecisionStates.ContractSummaryEcomm },
              })),
            },
            {
              type: 'final',
              actions: sendParent((context) => ({
                type: EcommMachineEvents.POST_DECISION,
                data: { ...context, postDecisionStartingPage: PostDecisionStates.ApprovalConfirmation },
              })),
            },
          ],
          [EntryPointEvents.DECLINED_OR_ERROR]: {
            type: 'final',
            actions: sendParent((_context, event) => ({
              type: EcommMachineEvents.LONG_FORM,
              data: { source: !!event.data.hardDeclineError ? 'Declined' : 'Error' },
            })),
          },
          [EntryPointEvents.COMPLETE_APP_LATER]: {
            target: EntryPointStates.Done,
            actions: sendParent((context) => ({
              type: EcommMachineEvents.LONG_FORM,
              data: { ...context, source: 'CompleteAppLater' },
            })),
          },
        },
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
          canGoBackViaHeader: false,
        },
      },

      [EntryPointStates.BankAccountError]: {
        id: 'BankAccountError',
        on: {
          [EntryPointEvents.NEXT]: {
            target: EntryPointStates.Done,
            actions: sendParent((context) => ({
              type: EcommMachineEvents.EXISTING_APPLICATION,
              data: { ...context, source: 'BankAccountError.NEXT' },
            })),
          },
        },
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
      },
      [EntryPointStates.DebitCardError]: {
        id: 'DebitCardError',
        on: {
          [EntryPointEvents.NEXT]: {
            target: EntryPointStates.Done,
            actions: sendParent((context) => ({
              type: EcommMachineEvents.EXISTING_APPLICATION,
              data: { ...context, source: 'DebitCardError.NEXT' },
            })),
          },
        },
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
      },
      [EntryPointStates.LoanDeclined]: {},
      [EntryPointStates.LeaseDeclined]: {},
      [EntryPointStates.Done]: { type: 'final' },
      // OR-2278: removed DraftAppReviewAndSubmit here; its only behavior was
      // firing transferToRapid() on entry, which no longer exists.
    },
    schema: {
      context: {} as EntryPointMachineContext,
      events: {} as EntryPointMachineEvents,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      // strictly for debugging, do not remove and make sure you clean up references in MRs
      log: (context, event) => {
        console.log('Entrypoint');
        console.log('context', context);
        console.log('event', event);
      },
    },
    guards: {
      PreApproval: (context: EntryPointMachineContext) => context.page === 'PreApproval',
      OTPRequest: (context: EntryPointMachineContext) => context.page === 'OTPRequest',
      BankAccountError: (context: EntryPointMachineContext) => context.page === 'BankAccountError',
      LeaseDeclined: (context: EntryPointMachineContext) => context.page === 'LeaseDecline',
      LoanDeclined: (context: EntryPointMachineContext) => context.page === 'LoadDecline',
      DebitCardError: (context: EntryPointMachineContext) => context.page === 'DebitCardError',
      isEcommLongForm: (context, _event) => {
        return (
          (context?.dealer?.formType === FormTypes.Long || context?.dealer?.formType === FormTypes.LongV2) &&
          context.dealer.channelType === ChannelTypes.Ecommerce
        );
      },
    },
  },
);
